import { useEffect } from 'react'
import { PostHogProvider } from 'posthog-js/react'
import { analytics } from '@/lib/services/analytics'
import { AnalyticsEvents } from '@/lib/services/analytics-events'
import { getSupabaseClient } from '@/lib/supabase'

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    analytics.init()
  }, [])

  useEffect(() => {
    const supabase = getSupabaseClient()
    if (!supabase) return

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        const traits: Record<string, unknown> = {
          email: session.user.email,
          phone: session.user.phone,
          auth_provider: session.user.app_metadata.provider,
        }

        // Identify immediately, then enrich with profile data
        analytics.identify(session.user.id, traits)

        // Fetch admin/approved status in the background — non-blocking
        Promise.resolve(
          supabase
            .from('profiles')
            .select('is_admin, is_approved')
            .eq('id', session.user.id)
            .single()
        )
          .then(({ data: profile }) => {
            if (profile) {
              analytics.identify(session.user.id, {
                ...traits,
                is_admin: profile.is_admin,
                is_approved: profile.is_approved,
              })
            }
          })
          .catch(() => {
            // Profile fetch failed — identify already happened with base traits
          })
      }
      if (event === 'SIGNED_OUT') {
        analytics.capture(AnalyticsEvents.AUTH_LOGOUT)
        analytics.reset()
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const instance = analytics.getPostHogInstance()

  if (!instance) return <>{children}</>

  return <PostHogProvider client={instance}>{children}</PostHogProvider>
}
